import { PrivacyType, PrivacyOption } from '../types/watchlist';
import { WatchlistPermissions, getPrivacyTypeInfo } from '../hooks/useWatchlistPermissions';

/**
 * Convert legacy privacy boolean to new privacy type
 */
export const getPrivacyTypeFromLegacy = (
  isPrivate: boolean, 
  hasCollaborators: boolean = false
): PrivacyType => {
  if (isPrivate && hasCollaborators) {
    return 'collaborative-private';
  } else if (!isPrivate && hasCollaborators) {
    return 'collaborative-public';
  } else if (isPrivate) {
    return 'private';
  } else {
    return 'public';
  }
};

/**
 * Convert privacy type to legacy boolean for backward compatibility
 */
export const getLegacyPrivacyFromType = (privacyType: PrivacyType): boolean => {
  return privacyType === 'private' || privacyType === 'collaborative-private';
};

/**
 * Get available privacy options based on current state and permissions
 */
export const getAvailablePrivacyOptions = (
  currentPrivacyType: PrivacyType,
  permissions: WatchlistPermissions,
  hasCollaborators: boolean = false
): PrivacyOption[] => {
  const options: PrivacyOption[] = [];

  // If user can't change privacy, return empty array
  if (!permissions.canChangePrivacy) {
    return options;
  }

  // Base options without collaborators
  if (!hasCollaborators) {
    options.push({
      value: 'private',
      label: 'Private',
      description: 'Only you can see this list',
      icon: 'Lock',
      recommended: currentPrivacyType === 'private'
    });

    options.push({
      value: 'public',
      label: 'Public',
      description: 'Anyone can see and like this list',
      icon: 'Public',
      recommended: currentPrivacyType === 'public'
    });
  } else {
    // Options with collaborators
    options.push({
      value: 'private',
      label: 'Private',
      description: 'Only you and collaborators can see this list',
      icon: 'Lock',
      recommended: currentPrivacyType === 'collaborative-private'
    });

    options.push({
      value: 'public',
      label: 'Public',
      description: 'Anyone can see and like this collaborative list',
      icon: 'Public',
      recommended: currentPrivacyType === 'collaborative-public'
    });
  }

  return options;
};

/**
 * Get the correct privacy type when toggling privacy with collaborators
 */
export const getTargetPrivacyType = (
  currentPrivacyType: PrivacyType,
  targetIsPrivate: boolean,
  hasCollaborators: boolean
): PrivacyType => {
  if (hasCollaborators) {
    return targetIsPrivate ? 'collaborative-private' : 'collaborative-public';
  } else {
    return targetIsPrivate ? 'private' : 'public';
  }
};

/**
 * Check if privacy change is valid
 */
export const isValidPrivacyChange = (
  fromType: PrivacyType,
  toType: PrivacyType,
  permissions: WatchlistPermissions
): boolean => {
  // Owner can always change privacy
  if (permissions.isOwner) {
    return true;
  }

  // Admin collaborators can change privacy
  if (permissions.isAdminCollaborator) {
    return true;
  }

  // Others cannot change privacy
  return false;
};

/**
 * Get privacy warning message when changing privacy
 */
export const getPrivacyChangeWarning = (
  fromType: PrivacyType,
  toType: PrivacyType,
  collaboratorCount: number = 0
): string | null => {
  // Making public when it was private
  if ((fromType === 'private' || fromType === 'collaborative-private') && 
      (toType === 'public' || toType === 'collaborative-public')) {
    if (collaboratorCount > 0) {
      return `Making this list public will allow anyone to see it and like it. Your ${collaboratorCount} collaborator${collaboratorCount === 1 ? '' : 's'} will still be able to edit it.`;
    } else {
      return 'Making this list public will allow anyone to see it and like it.';
    }
  }

  // Making private when it was public
  if ((fromType === 'public' || fromType === 'collaborative-public') && 
      (toType === 'private' || toType === 'collaborative-private')) {
    if (collaboratorCount > 0) {
      return `Making this list private will hide it from public discovery. Only you and your ${collaboratorCount} collaborator${collaboratorCount === 1 ? '' : 's'} will be able to see it.`;
    } else {
      return 'Making this list private will hide it from public discovery. Only you will be able to see it.';
    }
  }

  return null;
};

/**
 * Get privacy status display info
 */
export const getPrivacyDisplayInfo = (
  privacyType: PrivacyType,
  collaboratorCount: number = 0,
  followerCount: number = 0
) => {
  return getPrivacyTypeInfo(privacyType, collaboratorCount, followerCount);
};

/**
 * Check if a list should be visible in public feeds
 */
export const isVisibleInPublicFeeds = (privacyType: PrivacyType): boolean => {
  return privacyType === 'public' || privacyType === 'collaborative-public';
};

/**
 * Check if a list can be liked by public users
 */
export const canBePubliclyLiked = (privacyType: PrivacyType): boolean => {
  return privacyType === 'public' || privacyType === 'collaborative-public';
};

/**
 * Check if a list supports collaboration
 */
export const supportsCollaboration = (privacyType: PrivacyType): boolean => {
  return privacyType === 'collaborative-private' || privacyType === 'collaborative-public';
};

/**
 * Get default privacy type for new lists
 */
export const getDefaultPrivacyType = (userHasCollaborationEnabled: boolean = false): PrivacyType => {
  return userHasCollaborationEnabled ? 'collaborative-private' : 'private';
};

/**
 * Sanitize privacy data for API calls
 */
export const sanitizePrivacyForApi = (privacyType: PrivacyType) => {
  return {
    isPrivate: getLegacyPrivacyFromType(privacyType),
    privacyType,
    isPublic: !getLegacyPrivacyFromType(privacyType),
    isCollaborative: supportsCollaboration(privacyType)
  };
};

/**
 * Get privacy migration data when adding/removing collaborators
 */
export const getPrivacyMigrationData = (
  currentPrivacyType: PrivacyType,
  willHaveCollaborators: boolean
) => {
  // If currently no collaborators but will have some
  if (!supportsCollaboration(currentPrivacyType) && willHaveCollaborators) {
    const wasPrivate = getLegacyPrivacyFromType(currentPrivacyType);
    return {
      newPrivacyType: wasPrivate ? 'collaborative-private' : 'collaborative-public',
      migrationNeeded: true,
      message: `This list will become collaborative. ${wasPrivate ? 'It will remain private but' : 'It will stay public and'} collaborators will be able to edit it.`
    };
  }

  // If currently has collaborators but will have none
  if (supportsCollaboration(currentPrivacyType) && !willHaveCollaborators) {
    const isPrivate = getLegacyPrivacyFromType(currentPrivacyType);
    return {
      newPrivacyType: isPrivate ? 'private' : 'public',
      migrationNeeded: true,
      message: `This list will become non-collaborative. Only you will be able to edit it.`
    };
  }

  return {
    newPrivacyType: currentPrivacyType,
    migrationNeeded: false,
    message: null
  };
};

export default {
  getPrivacyTypeFromLegacy,
  getLegacyPrivacyFromType,
  getAvailablePrivacyOptions,
  getTargetPrivacyType,
  isValidPrivacyChange,
  getPrivacyChangeWarning,
  getPrivacyDisplayInfo,
  isVisibleInPublicFeeds,
  canBePubliclyLiked,
  supportsCollaboration,
  getDefaultPrivacyType,
  sanitizePrivacyForApi,
  getPrivacyMigrationData
}; 